import { toNumber } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    CronInput,
    errorRedirect,
    FormStore,
    inject,
    Input,
    Loader,
    ModalHandle,
    Nullable,
    SelectInput,
    SubTopBar,
    TasksStore,
    Title,
    toArray,
    upperCaseEachWord,
    uuidv4generate,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowLeftShort } from '@react-icons/all-files/bs/BsArrowLeftShort';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { Button, Form, FormGroup } from 'reactstrap';
import { defaultManifestColumns } from '../Account/AccountStore';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { DisplaySelectorCard } from '../DisplayFilters/DisplaySelectorCard';
import { DisplaysStore } from '../Displays/DisplaysStore';
import { FileSystemsStore } from './FileSystemsStore';
import { ManifestConfirmModal } from './ManifestConfirmModal';
import { ManifestsStore } from './ManifestsStore';

interface IFileSystemOption {
    value: string;
    label: string;
}

export interface IEditManifestLocationState {
    displayFilter?: Mri.DisplayFilters.DisplayFilter;
}

export interface IEditManifestParams {
    guid: string;
    serial: string;
}

export interface ICreateOrEditManifestProps
    extends RouteComponentProps<IEditManifestParams, {}, IEditManifestLocationState> {}

@autoinject
@observer
export class CreateOrEditManifest extends React.Component<ICreateOrEditManifestProps> {
    @computed
    private get fileSystemOptions(): IFileSystemOption[] {
        const fileSystems = toArray(this.fileSystemsStore.fileSystems)
            .filter(fs => fs.dependencies != null && fs.dependencies)
            .map(fs => {
                const fileName = fs.fileName;

                const lastScoreIndex = fileName.lastIndexOf('_');
                const filePart = fileName.substring(0, lastScoreIndex);
                const versionPart = fileName.substring(lastScoreIndex + 1);

                const versionSplit = versionPart.split('.');

                const versionMaj = toNumber(versionSplit[0]);
                const versionMin = toNumber(versionSplit[1]);
                const versionPat = toNumber(versionSplit[2]);

                return {
                    value: fs.fileSystemGuid,
                    label: fileName,
                    filePart,
                    versionMaj,
                    versionMin,
                    versionPat,
                };
            })
            .sort((a, b) => {
                return a.versionPat - b.versionPat;
            })
            .sort((a, b) => {
                return a.versionMin - b.versionMin;
            })
            .sort((a, b) => {
                return a.versionMaj - b.versionMaj;
            })
            .sort((a, b) => {
                return b.filePart > a.filePart ? 1 : -1;
            });

        return fileSystems;
    }

    @computed
    private get viewState() {
        return this.props.match.params.guid ? 'edit' : 'create';
    }

    @computed
    private get manifest() {
        return this.manifestsStore.manifests.get(this.props.match.params.guid);
    }

    public constructor(props: ICreateOrEditManifestProps) {
        super(props);

        this.vm = new DisplayListViewModel({
            onChange: this.onDisplayFilterChange,
            onClear: this.onDisplayFilterCleared,
            columns: defaultManifestColumns,
        });
    }

    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            await this.fileSystemsStore.loadAllAsync(true);
            if (this.viewState == 'edit') {
                await this.manifestsStore.loadAsync(this.props.match.params.guid);
            }

            this.fillForm();

            if (this.viewState == 'edit' && this.vm.displayFilter && !this.vm.displayFilter.displays?.length) {
                this.simpleDisplaySelector = false;
            }

            await this.vm.loadMoreAsync();
            await this.vm.loadContractsAsync();

            if (this.manifestForm?.values.displayFilter.displays && this.props.match.params.serial) {
                const display = this.displaysStore.displays.get(this.props.match.params.serial);
                this.displaysStore.displays.forEach(d => {
                    if (d.unitSerialNumber == display?.unitSerialNumber && d.serialNumber != display.serialNumber) {
                        this.vm.checkedSerialNumbers.push(d.serialNumber);
                    }
                });
            }
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div className="container-fluid pt-3">
                <Title>{this.viewState == 'edit' ? 'Edit' : 'Create'} Manifest</Title>

                <SubTopBar>
                    <LinkContainer exact to="/software-updates/manifests">
                        <a className="btn btn-link">
                            <BsArrowLeftShort style={{ fontSize: 25 }} /> Back To Manifests
                        </a>
                    </LinkContainer>
                </SubTopBar>

                <Loader
                    loading={this.loading && !this.manifestForm}
                    message=" Loading Manifest..."
                    showSpinner
                    className="w-100 text-center"
                />

                {this.manifestForm && (
                    <Form onSubmit={this.submitAsync}>
                        <div className="row justify-content-center">
                            <div className="col-12 col-md-6 col-lg-5 mb-3">
                                <div className="card">
                                    <div className="card-header bg-btn-primary">Manifest</div>
                                    <div className="card-body">
                                        {!!this.manifestForm.globalErrors.length && (
                                            <div className="alert-danger p-2">
                                                <ul>
                                                    {this.manifestForm.globalErrors.map((e: string, idx: number) => (
                                                        <li key={idx}>{e}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        <div>
                                            <FormGroup color={this.manifestForm.fields.name.state}>
                                                <label>
                                                    <strong>Name</strong>
                                                </label>

                                                <Input
                                                    type="text"
                                                    field={this.manifestForm.fields.name}
                                                    placeholder="Name"
                                                    required
                                                />
                                            </FormGroup>

                                            <FormGroup color={this.manifestForm.fields.comment.state}>
                                                <label>
                                                    <strong>Comments</strong>
                                                </label>

                                                <Input
                                                    type="text"
                                                    field={this.manifestForm.fields.comment}
                                                    placeholder="Comments"
                                                />
                                            </FormGroup>

                                            <FormGroup>
                                                <label>
                                                    <strong>File Systems</strong>
                                                </label>

                                                <SelectInput
                                                    className="react-select-container"
                                                    options={this.fileSystemOptions}
                                                    field={this.manifestForm.fields.fileSystems}
                                                    isMulti
                                                    required
                                                />
                                            </FormGroup>

                                            <CronInput field={this.manifestForm.fields.schedule} />

                                            <FormGroup color={this.manifestForm.fields.active.state}>
                                                <label>
                                                    <strong>Requires Pre-Reboot</strong>
                                                </label>

                                                <Input
                                                    type="checkbox"
                                                    field={this.manifestForm.fields.requiresPreReboot}
                                                    placeholder="RequiresPreReboot"
                                                    className="ms-3"
                                                />

                                                <br />

                                                <label>
                                                    <strong>Active</strong>
                                                </label>

                                                <Input
                                                    type="checkbox"
                                                    field={this.manifestForm.fields.active}
                                                    placeholder="Active"
                                                    className="ms-3"
                                                />
                                            </FormGroup>

                                            <FormGroup color={this.manifestForm.fields.emailRecipient.state}>
                                                <label>
                                                    <strong>Email Recipient</strong>
                                                </label>

                                                <Input
                                                    type="text"
                                                    field={this.manifestForm.fields.emailRecipient}
                                                    placeholder="email"
                                                />
                                            </FormGroup>

                                            <FormGroup>
                                                <Button
                                                    type="submit"
                                                    color="primary"
                                                    disabled={
                                                        !this.vm.checkedDisplays?.length &&
                                                        !this.vm.displayFilter.accounts?.length &&
                                                        !this.vm.displayFilter.displayGroups?.length &&
                                                        !this.vm.displayFilter.contracts?.length &&
                                                        !this.vm.displayFilter.nocServers?.length &&
                                                        !this.vm.displayFilter.partDefinitions?.length &&
                                                        !this.vm.displayFilter.cities?.length &&
                                                        !this.vm.displayFilter.stateOrProvinces?.length &&
                                                        !this.vm.displayFilter.countries?.length &&
                                                        !this.vm.displayFilter.contractServiceLevels?.length &&
                                                        !this.vm.displayFilter.currentSoftwareVersions?.length
                                                    }
                                                >
                                                    <Loader
                                                        loading={this.loading}
                                                        showSpinner={true}
                                                        message="Applying..."
                                                    />
                                                    {!this.loading && (
                                                        <span>
                                                            {this.viewState == 'create' && 'Create Manifest'}
                                                            {this.viewState == 'edit' && 'Update'}
                                                        </span>
                                                    )}
                                                </Button>
                                            </FormGroup>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 col-md-6 col-lg-7">
                                <DisplaySelectorCard
                                    columns={defaultManifestColumns}
                                    history={this.props.history}
                                    vm={this.vm}
                                />
                            </div>

                            <ManifestConfirmModal
                                vm={this.vm}
                                modalHandle={this.modalHandle}
                                manifestGuid={this.props.match.params.guid}
                            />
                        </div>
                    </Form>
                )}
            </div>
        );
    }

    @action
    private fillForm() {
        if (this.viewState == 'edit' && !this.manifest) throw new Error('Manifest does not exist');

        if (this.viewState == 'edit' && this.manifest) {
            this.manifestForm = new FormStore<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>({
                ...this.manifest,
                softwareUpdateManifestGuid: uuidv4generate(),
                version: this.manifest.version + 1,
            });

            this.vm = new DisplayListViewModel({
                onChange: this.onDisplayFilterChange,
                onClear: this.onDisplayFilterCleared,
                displayFilter: this.manifestForm.values.displayFilter,
                columns: defaultManifestColumns,
                removeCheckedOnChange: false,
            });
        } else if (this.viewState == 'create' && this.props.match.params.serial) {
            const model: Nullable<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest> = {
                name: 'Update ' + this.props.match.params.serial,
                comment: '',
                schedule: '* * * * *',
                active: false,
                softwareUpdateManifestGuid: uuidv4generate(),
                displayFilter: {
                    displays: [this.props.match.params.serial],
                    includeSiblings: false,
                },
                fileSystems: [],
                versionId: uuidv4generate(),
                version: 0,
                archived: false,
                emailRecipient: '',
                requiresPreReboot: true,
            };

            this.manifestForm = new FormStore<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>(model);

            this.vm = new DisplayListViewModel({
                onChange: this.onDisplayFilterChange,
                onClear: this.onDisplayFilterCleared,
                displayFilter: this.manifestForm.values.displayFilter,
                columns: defaultManifestColumns,
                removeCheckedOnChange: false,
            });
        } else {
            const model: Nullable<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest> = {
                name: '',
                comment: '',
                schedule: '',
                active: false,
                softwareUpdateManifestGuid: uuidv4generate(),
                displayFilter: {
                    displays: [],
                    includeSiblings: this.simpleDisplaySelector == false ? true : false,
                },
                fileSystems: [],
                versionId: uuidv4generate(),
                version: 0,
                archived: false,
                emailRecipient: '',
                requiresPreReboot: true,
            };

            if (this.props.location?.state?.displayFilter)
                Object.assign(model.displayFilter!, this.props.location.state.displayFilter); //TODO TEST UPGRADE

            this.manifestForm = new FormStore<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>(model);
            this.vm = new DisplayListViewModel({
                onChange: this.onDisplayFilterChange,
                onClear: this.onDisplayFilterCleared,
                displayFilter: model.displayFilter!,
                columns: defaultManifestColumns,
                removeCheckedOnChange: false,
            });
        }

        this.vm.checkedSerialNumbers = this.manifestForm?.values.displayFilter.displays || [];
        this.vm.displayFilter.includeSiblings = true;
    }

    @bind
    @action
    private async submitAsync(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!this.manifestForm) throw new Error('Form does not exist');
        if (this.vm.checkedDisplays.length > 0) {
            this.vm.checkedSerialNumbers = this.vm.checkedSerialNumbers.filter(d => {
                return d !== undefined;
            });

            this.manifestForm.fields.displayFilter.fields.displays.setValue(
                this.vm.checkedDisplays.map(d => d.serialNumber)
            );
        } else {
            this.manifestForm.fields.displayFilter.fields.displays.setValue(this.vm.displayFilter.displays);
        }

        this.manifestForm.onSubmit();
        if (this.manifestForm.validate()) {
            this.loading = true;

            this.vm.displayFilter.includeSiblings = true;

            if (await this.modalHandle.confirm()) {
                const task = this.tasksStore.startSessionTask(upperCaseEachWord(this.viewState) + ' Manifest');

                try {
                    if (this.viewState == 'edit') {
                        await this.manifestsStore.updateAsync(this.props.match.params.guid, this.manifestForm.values);
                    } else if (this.viewState == 'create') {
                        await this.manifestsStore.createAsync(this.manifestForm.values);
                    }
                    task.complete();

                    if (this.viewState == 'create' && this.props.match.params.serial) {
                        this.props.history.push(
                            '/software-updates/manifests/' +
                                this.manifestForm.values.softwareUpdateManifestGuid +
                                '/status'
                        );
                    } else {
                        this.props.history.push('/software-updates/manifests/');
                    }
                } catch (e: any) {
                    if (e.modelState) {
                        this.manifestForm.clearGlobalErrors();
                        this.manifestForm.addErrors(e.modelState);
                    }

                    task.error(e);
                } finally {
                    this.loading = false;
                }
            } else {
                this.loading = false;
            }
        } else {
            console.log('invalid');
            this.loading = false;
        }
    }

    @bind
    @action
    private onDisplayFilterChange(displayFilter: Mri.DisplayFilters.DisplayFilter) {
        if (!this.manifestForm) throw new Error('Form does not exist');

        this.manifestForm.fields.displayFilter.setValue(displayFilter);
    }

    @bind
    @action
    private onDisplayFilterCleared() {
        this.vm.displayFilter.includeSiblings = this.simpleDisplaySelector == false ? true : false;
    }

    private vm!: DisplayListViewModel;
    private modalHandle = new ModalHandle();
    @observable private manifestForm?: FormStore<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>;
    @observable private simpleDisplaySelector = true;
    @observable private loading = false;

    @inject private readonly manifestsStore!: ManifestsStore;
    @inject private readonly displaysStore!: DisplaysStore;
    @inject private readonly fileSystemsStore!: FileSystemsStore;
    @inject private readonly tasksStore!: TasksStore;
}
